import React from 'react';
import { styled } from 'styled-components';
import { ChevronIcon as ArrowDown } from 'src/components/widgets/icons/scene';
import { Icon } from 'src/components/widgets/icon';
import { useEffect, useRef } from 'react';
import { useSectionOpen } from './propertiesPanelSectionMemory';
import { useIsSceneLook } from './propertiesPanelLook';
import { panel } from './panelTheme';
import { PropertiesPanelPropertyContainer } from './propertiesPanelProperty';

export const PropertiesPanelAccordionContainer = styled.div`
    font-family: ${(props) => props.theme.app.font};
`;

export const PropertiesPanelAccordionHeader = styled.div`
    cursor: pointer;
    font-size: 13px;
    font-weight: bold;
    display: flex;
    gap: 8px;
    align-items: center;
    display: flex;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    padding: 6px 0px;

    ${Icon} {
        margin-left: auto;
        color: #445a64;
    }
`;

export const PropertiesPanelAccordionContent = styled.div<{ $isOpen: boolean }>`
    max-height: ${(props) => (props.$isOpen ? '3000px' : '0')};
    visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
    transition: max-height 300ms;
    overflow: hidden;
`;

// LA CARD DELLA SCENA. Un gruppo di parametri è una superficie sua, non una riga con una linea
// sotto: in una colonna lunga le linee si sommano fino a diventare una grata, mentre le card si
// contano a colpo d'occhio.
//
// LA FRECCIA STA A SINISTRA, prima del nome. A destra ci va ciò che comanda il gruppo — un
// interruttore, dove il gruppo intero si può spegnere — e le due cose non possono contendersi lo
// stesso angolo.
// IL RIENTRO DELLA CARD STA SULLA CARD, e da lì in poi ogni cosa dentro misura da quel bordo.
// Prima non ce n'era: lo scriveva ogni figlio del corpo, dieci pixel per uno, e la testata ne
// scriveva altri dieci per conto suo — tre sorgenti per lo stesso margine, che è il modo in cui
// una card comincia a respirare in un punto e a soffocare in un altro.
const SceneCard = styled.div`
    border: 1px solid ${panel.line};
    border-radius: 8px;
    background: ${panel.surface};
    padding: 6px 8px;
    overflow: hidden;

    & + & {
        margin-top: 10px;
    }
`;

// IL BERSAGLIO DELLA FRECCIA E QUELLO DELL'INTERRUTTORE SONO FRATELLI, non uno dentro l'altro:
// un interruttore annidato nella testata cliccabile aprirebbe il gruppo ogni volta che lo si
// spegne, e il rimedio abituale — fermare il clic — è una riga che se sparisce rompe il
// componente in silenzio.
const SceneHeadRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SceneHead = styled.button<{ $isOpen: boolean; $fixed?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;

    /* IL BERSAGLIO NON DIPENDE DALLE METRICHE DEL CARATTERE: ventotto pixel dichiarati, così una
       testata alta quanto la sua parola non diventa una riga da centrare col puntatore. Il rientro
       di sei è quello che allinea il chevron al contenuto del corpo — è la stessa colonna. */
    min-height: 28px;
    padding: 0 6px;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: ${panel.ink};
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    text-align: left;
    cursor: ${(props) => (props.$fixed ? 'default' : 'pointer')};

    ${Icon} {
        flex: 0 0 auto;
        width: 10px;
        color: ${panel.inkMuted};
        transform: rotate(${(props) => (props.$isOpen ? '0deg' : '-90deg')});
        transition: transform 160ms ease;
    }

    /* La testata è la maniglia del gruppo: al passaggio si accende come una riga di elenco. */
    &:hover {
        background: color-mix(in srgb, ${panel.ink} 4%, transparent);
    }
`;

// IL PASSO FRA DUE RIGHE LO TIENE IL CORPO, UNA VOLTA SOLA. Scritto sulle righe erano tanti
// margini quante specie di riga — un campo, un interruttore, un canale — e bastava aggiungerne
// una perché la colonna cambiasse ritmo a metà. Qui è un numero solo, e vale per tutto ciò che
// una card può contenere.
//
// IL RIENTRO SI CHIUDE INSIEME AL CORPO: con l'altezza a zero ma il rientro fermo, una card
// chiusa resterebbe alta otto pixel di niente.
// LA COLONNA DEI NOMI SE LA MISURA LA CARD, e ogni card la misura sulle proprie etichette.
// Era fissa — 89 pixel, tarati su un nome accanto a un controllo solo — e un nome di due
// parole come «Environment map» o «Shadow opacity» ci arrivava al limite: sopra quella misura
// andava a capo anche col pannello largo. Il tetto del 50% resta, ed è l'ordine di sempre: se
// proprio si deve cedere, cede il nome, perché un nome su due righe si legge ancora e un
// controllo schiacciato no.
//
// SONO LE CARD A MISURARSI, NON IL PANNELLO INTERO: sono gruppi separati da una testata, e una
// colonna sola per tutta la colonna di destra farebbe pagare a ogni card il nome più lungo di
// una card che sta dieci righe più giù.
//
// Le righe che NON sono figlie dirette di questo corpo — quelle dentro un blocco o una card
// annidata — restano alla colonna fissa: dentro un blocco le etichette sono corte, e una
// colonna misurata per blocco farebbe partire ogni blocco da una x diversa.
const SceneBody = styled.div<{ $isOpen: boolean }>`
    display: grid;
    grid-template-columns: fit-content(50%) minmax(0, 1fr);
    align-content: start;
    column-gap: 10px;
    row-gap: 10px;
    max-height: ${(props) => (props.$isOpen ? '3000px' : '0')};
    visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
    padding: ${(props) => (props.$isOpen ? '8px 6px 12px' : '0 6px')};
    transition:
        max-height 300ms,
        padding 300ms;
    overflow: hidden;

    /* Ciò che non è una riga di parametro — una card annidata, un blocco, un'immagine — prende
       tutte e due le colonne. */
    > * {
        grid-column: 1 / -1;
    }

    /* IL PASSO FRA LE COLONNE LO DETTA IL CORPO, non la riga: una subgrid può sovrascrivere la
       gronda che eredita, e due righe con due gronde diverse fanno partire i loro controlli da
       due x diverse — che è esattamente il difetto che questa griglia esiste per togliere.
       LE RIGHE \`lone\` NON ENTRANO NELLA MISURA: il loro nome si prende lo spazio che il comando
       non usa, e contarlo allargherebbe la colonna di tutte le altre. */
    > ${PropertiesPanelPropertyContainer}:not([data-lone]) {
        display: grid;
        grid-template-columns: subgrid;
        column-gap: 10px;
    }

    /* Una card dentro una card prende il passo dal gap come tutti gli altri figli: il proprio
       margine servirebbe fuori di qui, dove le card non hanno un contenitore che le spazia. */
    > ${SceneCard} + ${SceneCard} {
        margin-top: 0;
    }
`;

export const PropertiesPanelAccordion = (props: {
    defaultIsOpen?: boolean;
    /**
     * IL NOME CON CUI LA SEZIONE SI RICORDA CHIUSA (`propertiesPanelSectionMemory`): stabile e non tradotto
     * — «materials», «size». Senza, la sezione dimentica come prima, appena esce dallo schermo.
     */
    memoryId?: string;
    /**
     * APERTA COMUNQUE, finché vale: dentro c'è un errore da correggere, e una sezione chiusa da una scelta
     * di ieri non deve nasconderlo. Quando l'errore se ne va, torna com'era stata lasciata.
     */
    forceOpen?: boolean;
    header: React.ReactNode;
    /** Ciò che comanda il gruppo intero: un interruttore, un segmentato. Solo nel vestito della scena. */
    trailing?: React.ReactNode;
    /**
     * Perché quello che c'è qui dentro si comporta in modo inatteso. Sta FUORI dalla maniglia,
     * come l'interruttore: chiedere una spiegazione non è chiedere di chiudere il gruppo.
     */
    hint?: React.ReactNode;
    /**
     * QUANDO IL GRUPPO SI ACCENDE, SI APRE. Si accende una funzione per usarla, e trovarla
     * ancora chiusa vorrebbe dire due gesti per uno. Spegnendola invece **non si richiude**:
     * chi spegne sta guardando quello che spegne, e un pannello che si chiude sotto le dita
     * toglie di vista la cosa appena toccata.
     *
     * Lo dice chi monta l'interruttore, perché è lui che sa quale stato governa il gruppo: da
     * qui dentro `trailing` è un nodo React qualunque, e indovinarne il valore vorrebbe dire
     * leggere dentro un pezzo di interfaccia che non ci appartiene.
     */
    openWhen?: boolean;
    /**
     * UNA CARD CHE NON SI APRE, perché non ha dentro niente da aprire: quello che dice sta tutto
     * nella testata — il tipo di una luce, una parola e basta. La freccia sparisce e la testata
     * smette di essere un pulsante: una maniglia che non porta da nessuna parte è una promessa
     * rotta, e un pulsante che non fa niente lo è due volte.
     */
    fixed?: boolean;
    children: React.ReactNode;
}) => {
    const [rememberedOpen, setIsOpen] = useSectionOpen(props.memoryId, props.defaultIsOpen ?? true);
    const isOpen = rememberedOpen || Boolean(props.forceOpen);
    const scene = useIsSceneLook();

    // Si apre al PASSAGGIO da spento ad acceso, non finché è acceso: altrimenti un gruppo già
    // acceso non si potrebbe più chiudere, si riaprirebbe al ridisegno successivo.
    const wasOn = useRef(props.openWhen);

    useEffect(() => {
        if (props.openWhen && !wasOn.current) setIsOpen(true);
        wasOn.current = props.openWhen;
    }, [props.openWhen]);

    if (scene) {
        return (
            <SceneCard>
                <SceneHeadRow>
                    {props.fixed ? (
                        <SceneHead as='span' $isOpen={false} $fixed>
                            {props.header}
                        </SceneHead>
                    ) : (
                        <SceneHead type='button' $isOpen={isOpen} aria-expanded={isOpen} onClick={() => setIsOpen((prev) => !prev)}>
                            <Icon>
                                <ArrowDown />
                            </Icon>
                            {props.header}
                        </SceneHead>
                    )}
                    {props.hint}
                    {props.trailing}
                </SceneHeadRow>
                {!props.fixed && <SceneBody $isOpen={isOpen}>{props.children}</SceneBody>}
            </SceneCard>
        );
    }

    return (
        <PropertiesPanelAccordionContainer>
            <PropertiesPanelAccordionHeader onClick={() => setIsOpen((prev) => !prev)}>
                {props.header}
                {props.hint}
                <Icon>
                    <ArrowDown />
                </Icon>
            </PropertiesPanelAccordionHeader>
            <PropertiesPanelAccordionContent $isOpen={isOpen}>{props.children}</PropertiesPanelAccordionContent>
        </PropertiesPanelAccordionContainer>
    );
};
